// AppLockControls — Settings panel for app lock status and actions.

import { ShieldCheck, ShieldOff, Lock } from 'lucide-react';
import { useState } from 'react';
import { useAppLock } from '../AppLock';

export function AppLockControls() {
  const { lock, isEnabled, isConfigured, enableEncryption } = useAppLock();
  const [showChange, setShowChange] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!lock) return;
    setError(null);
    if (next.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      await lock.changePassword(current, next);
      setShowChange(false);
      setCurrent('');
      setNext('');
    } catch {
      setError('Current password is incorrect.');
    } finally {
      setBusy(false);
    }
  }

  if (isEnabled && isConfigured) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} aria-hidden="true" style={{ color: 'var(--color-success)' }} />
          <span className="text-sm">
            Encryption active — your sessions, profiles, and bag are encrypted.
          </span>
        </div>

        {showChange ? (
          <form onSubmit={handleChangePassword} className="flex flex-col gap-3 panel-raised">
            <label className="field-label" htmlFor="al-current">Current password</label>
            <input
              id="al-current"
              type="password"
              className="field-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
            <label className="field-label" htmlFor="al-next">New password</label>
            <input
              id="al-next"
              type="password"
              className="field-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
            {error && (
              <p className="text-xs" style={{ color: 'var(--color-danger)' }} role="alert">{error}</p>
            )}
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={busy || !current || !next}>
                {busy ? 'Changing…' : 'Change Password'}
              </button>
              <button type="button" className="btn" onClick={() => { setShowChange(false); setError(null); }} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn"
              onClick={() => lock?.lock('manual')}
            >
              <Lock size={16} aria-hidden="true" />
              Lock Now
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setShowChange(true)}
            >
              Change Password
            </button>
          </div>
        )}
      </div>
    );
  }

  if (isEnabled && !isConfigured) {
    return (
      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
        Complete the password setup to activate encryption.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <ShieldOff size={18} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No password protection — your data is stored unencrypted.
        </span>
      </div>
      <button type="button" className="btn btn-primary" onClick={enableEncryption}>
        <ShieldCheck size={16} aria-hidden="true" />
        Enable App Lock
      </button>
    </div>
  );
}
